import { IonText } from '@ionic/react';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import ActionIconButton from '@components/buttons/ActionIconButton';
import styles from '@components/ContentCondition.module.css';
import DeleteConfirmationAlert from '@components/DeleteConfirmationAlert';
import { FormData as ContentConditionFormData } from '@components/forms/ContentConditionForm';
import useMutationWithValidation from '@hooks/useMutationWithValidation';
import useOpener from '@hooks/useOpener';
import {
  ContentConditionDestroyDocument,
  ContentConditionDestroyMutation,
  ContentConditionDestroyMutationVariables,
  ContentConditionKindEnum,
  ContentConditionFragment as ContentConditionType
} from '@typing/Generated';
import { snakeToCamel } from '@utils/stringUtils';

type Props = {
  contentCondition: ContentConditionType | ContentConditionFormData;
  onDelete?: (value?: string, contentCondition?: ContentConditionType | ContentConditionFormData) => void;
};

const ContentCondition = ({ contentCondition, onDelete }: Props) => {
  const intl = useIntl();
  const deleteOpener = useOpener();
  const [destroyContentCondition, client] = useMutationWithValidation<
    ContentConditionDestroyMutation,
    ContentConditionDestroyMutationVariables
  >(ContentConditionDestroyDocument, 'contentCondition', {
    update: () => {
      // This sucks a little... we can update the article in the cache to remove the condition,
      // but there's also the query for the full condition list, and we cannot update that here.
      client.resetStore();
    }
  });

  const handleDelete = useCallback(() => {
    if (!contentCondition.id) return;
    if (onDelete) {
      onDelete(contentCondition.id, contentCondition);
    } else if (contentCondition.id) {
      destroyContentCondition({
        id: contentCondition.id
      });
    }
  }, [contentCondition, destroyContentCondition, onDelete]);

  const displayValue = useMemo(() => {
    if (contentCondition.kind === ContentConditionKindEnum.provider_emotional_state_selected) {
      return intl.formatMessage({
        id: `models.careProfile.providerEmotionalStateOptions.${contentCondition.providerEmotionalState ?? ''}`
      });
    }
    if (contentCondition.kind === ContentConditionKindEnum.provider_feels_supported_selected) {
      return intl.formatMessage({
        id: `models.carePofile.providerFeelsSupportedOptions.${contentCondition.providerFeelsSupported ?? ''}`
      });
    }
    if (contentCondition.kind === ContentConditionKindEnum.stage_selected) {
      return intl.formatMessage({
        id: `models.journey.stage.${contentCondition.stage ?? ''}`
      });
    }
    if (contentCondition.kind === ContentConditionKindEnum.relationship_selected) {
      return intl.formatMessage({ id: `models.clientRole.relationship.${contentCondition.relationship ?? ''}` });
    }
    return null;
  }, [contentCondition, intl]);

  return (
    <>
      <div className={styles.item}>
        <div className={styles.icon}>
          <ActionIconButton ariaLabelKey="accessibility.ariaLabels.delete" icon="trash" onClick={deleteOpener.open} />
        </div>
        <div>
          <IonText className="ion-margin-start">
            {intl.formatMessage({ id: `models.contentCondition.source.${contentCondition.kind}` })}
            {contentCondition.kind !== ContentConditionKindEnum.always && (
              <>
                <FormattedMessage id="pages.admin.article.seperator" />
                {contentCondition.kind === ContentConditionKindEnum.care_profile_field_value ? (
                  <>
                    <FormattedMessage
                      id={`models.careProfile.${snakeToCamel(contentCondition.careProfileField) ?? ''}`}
                    />
                    <FormattedMessage id="pages.admin.article.seperator" />
                    <FormattedMessage
                      id={`models.careProfile.${snakeToCamel(contentCondition.careProfileField) ?? ''}Options.${
                        contentCondition.careProfileValue ?? ''
                      }`}
                    />
                  </>
                ) : (
                  <>
                    {intl.formatMessage({ id: `models.contentCondition.kind.${contentCondition.kind}` })}
                    {displayValue && (
                      <>
                        <FormattedMessage id="pages.admin.article.seperator" />
                        {displayValue}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </IonText>
        </div>
      </div>
      <DeleteConfirmationAlert
        messageKey="pages.admin.article.reallyDelete"
        onDelete={handleDelete}
        opener={deleteOpener}
      />
    </>
  );
};

export default ContentCondition;
